import {parseVersionFromSnapshotPath} from '../features/products/previewPage/useReportDocumentSubscription';
import {apiMetaToHeaderData, mergeHeaderData} from './documentHeaderMeta';
import {exportReportToDocx} from './docxExportService';
import {sanitizeFileName} from '../features/products/reportEditorPage/reportHeader/utils';
import {downloadLatestVersionFromApi, downloadReportVersionFromApi} from './api/reportVersions';
import {normalizePmDoc} from '../features/products/disclosureEditorPage/leftPanel/generateInitialVersion/generationUtils';
import {resolveExportSnapshot} from './resolveExportSnapshot';
import {resolveDocumentAuthors} from './resolveDocumentAuthors';
import {
    getSnapshotCache,
    invalidateSnapshotCache,
    setSnapshotCache
} from './reportSnapshotCache';

/**
 * Normalize a version identifier for comparison, regardless of zero-padding.
 * `"7"`, `7`, and `"0000007"` should all be considered the same version.
 * Falls back to a plain string comparison for non-numeric identifiers.
 */
function normalizeVersionForComparison(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : String(value ?? '');
}

/**
 * Load content, references and metadata from a Y.Doc snapshot.
 * Returns null if snapshotPath is missing or version cannot be parsed.
 */
export async function loadSnapshotData(reportId, snapshotPath) {
    if (!snapshotPath) return null;

    const version = parseVersionFromSnapshotPath(snapshotPath);
    if (!version) return null;

    const cached = getSnapshotCache(reportId, version);
    if (cached) {
        const {references: snapRefs, metadata, version: _v, reportId: _r, ...content} = cached;
        return {
            content,
            references: snapRefs || [],
            metadata: metadata || null,
        };
    }

    const payload = await downloadReportVersionFromApi(reportId, version);

    const resolvedVersion = payload?.version ?? version;
    const resolvedReportId = String(payload?.reportId ?? reportId);
    const versionMatches = normalizeVersionForComparison(resolvedVersion) === normalizeVersionForComparison(version);
    if (!versionMatches || resolvedReportId !== String(reportId)) {
        invalidateSnapshotCache(reportId, version);
        throw new Error(
            `Snapshot payload mismatch for requested report/version: `
            + `requested reportId=${reportId} version=${version}, `
            + `received reportId=${resolvedReportId} version=${resolvedVersion}`
        );
    }

    setSnapshotCache(reportId, version, payload);

    const {references: snapRefs, metadata, version: _v, reportId: _r, ...content} = payload;

    return {
        content,
        references: snapRefs || [],
        metadata: metadata || null,
    };
}

const extractTextFromPmDoc = (node) => {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(extractTextFromPmDoc).filter(Boolean).join('\n');

    const chunks = [];
    if (typeof node.text === 'string') chunks.push(node.text);
    if (Array.isArray(node.content)) {
        node.content.forEach((child) => {
            const childText = extractTextFromPmDoc(child);
            if (childText) chunks.push(childText);
        });
    }
    return chunks.join('\n');
};

/**
 * Load plain-text pitch body for ICR generation.
 * Prefers legacy snapshotPath, then latest persisted report version (REST API),
 * then any in-memory fallback content already in the report store.
 */
export async function loadPitchTextFromReport(reportId, snapshotPath, fallbackContent) {
    const fromLegacySnapshot = await loadSnapshotData(reportId, snapshotPath).catch((err) => {
        console.error('Error loading legacy report snapshot for pitch text:', err);
        return null;
    });
    const legacyText = extractTextFromPmDoc(normalizePmDoc(fromLegacySnapshot?.content)).trim();
    if (legacyText) return legacyText;

    const latestSnapshot = reportId
        ? await downloadLatestVersionFromApi(reportId).catch(() => null)
        : null;
    const latestText = extractTextFromPmDoc(normalizePmDoc(latestSnapshot?.content)).trim();
    if (latestText) return latestText;

    return extractTextFromPmDoc(normalizePmDoc(fallbackContent)).trim();
}

/**
 * Merge base metadata (Firestore / Zustand) with snapshot metadata (Y.Doc)
 * into a single meta object suitable for DOCX export.
 */
export function buildExportMeta(baseMeta, snapshotMeta, title, reportId) {
    const merged = {
        ...(snapshotMeta || {}),
        ...(baseMeta || {}),
        reportTitle: title || baseMeta?.title || snapshotMeta?.title,
        reportId,
    };

    const preferFilled = (baseValue, snapshotValue) => {
        if (Array.isArray(baseValue) && baseValue.length > 0) return baseValue;
        if (Array.isArray(snapshotValue) && snapshotValue.length > 0) return snapshotValue;
        if (baseValue !== null && baseValue !== undefined && baseValue !== '') return baseValue;
        return snapshotValue;
    };

    const flat = {
        ...merged,
        title: preferFilled(baseMeta?.title, snapshotMeta?.title),
        authors: preferFilled(baseMeta?.authors, snapshotMeta?.authors) || [],
        dtgIso: preferFilled(baseMeta?.dtgIso, snapshotMeta?.dtgIso),
        dtg: preferFilled(baseMeta?.dtg, snapshotMeta?.dtg),
        originator: preferFilled(baseMeta?.originator, snapshotMeta?.originator),
        classification: preferFilled(baseMeta?.classification, snapshotMeta?.classification),
        distribution: preferFilled(baseMeta?.distribution, snapshotMeta?.distribution),
        compartment: preferFilled(baseMeta?.compartment, snapshotMeta?.compartment),
        confidence: preferFilled(baseMeta?.confidence, snapshotMeta?.confidence),
        ifc: preferFilled(baseMeta?.ifc, snapshotMeta?.ifc),
        productionCenter: preferFilled(baseMeta?.productionCenter, snapshotMeta?.productionCenter),
        icrNumber: preferFilled(baseMeta?.icrNumber, snapshotMeta?.icrNumber),
        productType: preferFilled(baseMeta?.productType, snapshotMeta?.productType),
    };

    return {
        ...flat,
        documentHeader: mergeHeaderData(
            apiMetaToHeaderData(snapshotMeta),
            apiMetaToHeaderData(baseMeta),
        ),
    };
}

/**
 * Build export metadata aligned with preview (Yjs header + content-derived authors).
 */
export async function buildExportMetaAsync({
    baseMeta,
    snapshotMeta,
    headerData,
    content,
    title,
    reportId,
}) {
    const documentHeader = mergeHeaderData(
        headerData || apiMetaToHeaderData(snapshotMeta),
        apiMetaToHeaderData(baseMeta),
    );

    const authors = await resolveDocumentAuthors({
        content,
        metaAuthors: baseMeta?.authors,
        headerAuthors: documentHeader?.authors,
    });

    return {
        reportTitle: title || baseMeta?.title || snapshotMeta?.title,
        reportId,
        title: title || baseMeta?.title || snapshotMeta?.title,
        icrNumber: baseMeta?.icrNumber ?? snapshotMeta?.icrNumber,
        productType: baseMeta?.productType ?? snapshotMeta?.productType,
        documentHeader,
        authors,
    };
}

/**
 * Trigger a browser download of a Blob as a .docx file.
 */
export function downloadDocxBlob(blob, title, reportId, fallbackPrefix = 'report') {
    const safeTitle = sanitizeFileName(title) || `${fallbackPrefix}_${reportId}`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeTitle}.docx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Full export flow used by table-row action hooks:
 * REST API (Phase 3) → legacy snapshotPath → report.content fallback → generate DOCX → download.
 */
export async function exportReport({reportId, title, report, fallbackPrefix = 'report'}) {
    const snapshot = await resolveExportSnapshot(reportId, null);

    if (snapshot?.content) {
        const meta = await buildExportMetaAsync({
            baseMeta: report?.meta,
            snapshotMeta: snapshot.metadata,
            headerData: snapshot.metadata,
            content: snapshot.content,
            title,
            reportId,
        });
        const blob = await exportReportToDocx(snapshot.content, meta, snapshot.references || [], title);
        downloadDocxBlob(blob, title, reportId, fallbackPrefix);
        return;
    }

    // Legacy fallback: snapshot path via MinIO.
    const legacySnapshot = await loadSnapshotData(reportId, report?.snapshotPath).catch((err) => {
        console.error('Error loading legacy report snapshot for export:', err);
        return null;
    });

    const content = legacySnapshot?.content || report?.content;
    if (!content) {
        throw new Error('No document content available for export');
    }

    const references = legacySnapshot?.references || [];
    const meta = await buildExportMetaAsync({
        baseMeta: report?.meta,
        snapshotMeta: legacySnapshot?.metadata,
        content,
        title,
        reportId,
    });

    const blob = await exportReportToDocx(content, meta, references, title);
    downloadDocxBlob(blob, title, reportId, fallbackPrefix);
}
